import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import { SpeakersStoreContext } from "../../Store/SpeakersStore";
import { Box, Button, Typography, Chip } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import dayjs from "dayjs";
import { getSpeakers } from "../../Requests/SpeakerService.ts";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import AddModalView from "../AddModalView/AddModalView";
import SpeakersAdd from "./SpeakersAdd";
import { VISIBLE_STATE_CASES } from "../../Models/StateConstants.ts";
import { useRestoreScrollPosition } from "../../Hooks/useScrollRestoration";
import { useTableState } from "../../Hooks/useTableState";
import { useFilteredBySearch } from "../../Hooks/useFilteredBySearch";
import TableToolbarWithSearch from "../TableToolbar/TableToolbarWithSearch.jsx";
import ColumnsIcon from "../TableToolbar/ColumnsIcon.jsx";


const COLUMNS = [
  {
    accessorFn: (originalRow) => originalRow.firstName + " " + originalRow.lastName,
    header: 'Speaker',
    Cell: ({ renderedCellValue, row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <img
          alt="avatar"
          height={32}
          width={32}
          src={row.original.avatar}
          loading="lazy"
          style={{ borderRadius: '50%' }}
        />
        <span>{renderedCellValue}</span>
      </Box>
    )
  },
  {
    accessorKey: 'occupation',
    header: 'Occupation',
    enableGlobalFilter: false,
  },
  {
    accessorFn: (originalRow) => new Date(originalRow.createdAt),
    accessorKey: 'createdAt',
    header: 'Date Created',
    filterVariant: 'date-range',
    enableGlobalFilter: false,
    Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
  },
  {
    accessorKey: 'sortOrder',
    header: 'Sort Order',
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    accessorKey: 'hiddenState',
    header: 'Visibility',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: VISIBLE_STATE_CASES.map((value) => value.label),
    Cell: ({ cell }) => (
      <Chip label={cell.getValue()} />
    )
  }
]

const SEARCH_FIELD_OPTIONS = [
  { value: 'fullName', label: 'Speaker' },
  { value: 'occupation', label: 'Occupation' }
]

export default function Speakers() {

  const store = useContext(SpeakersStoreContext)

  const [columnVisibility, setColumnVisibility] = useState(store.columnVisibility)
  useEffect(() => {
    store.columnVisibility = columnVisibility
  }, [columnVisibility, store])

  const [columnFilters, setColumnFilters] = useState(store.columnFilters)
  useEffect(() => {
    store.columnFilters = columnFilters
  }, [columnFilters, store])
  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const [searchField, setSearchField] = useTableState(store, 'searchField', SEARCH_FIELD_OPTIONS[0].value)

  const [pagination, setPagination] = useState(store.pagination)
  useEffect(() => {
    store.pagination = pagination
  }, [pagination, store])

  const navigate = useNavigate()
  const tableContainerRef = useRef(null)

  // Use store for state preservation
  const [open, setOpen] = useState(store.open)
  useEffect(() => {
    store.open = open
  }, [open, store])

  const [isLoading, setIsloading] = useState(store.isLoading)
  useEffect(() => {
    store.isLoading = isLoading
  }, [isLoading, store])

  const [isRefetching, setIsRefetching] = useState(store.isRefetching)
  useEffect(() => {
    store.isRefetching = isRefetching
  }, [isRefetching, store])

  const [tableData, setTableData] = useState(store.tableData)
  useEffect(() => {
    store.tableData = tableData
  }, [tableData, store])

  const fetchData = useCallback(async () => {
    const result = await getSpeakers()
    setIsloading(false)
    setIsRefetching(false)
    setTableData(result)
  }, [])

  useEffect(() => {
    // Restore state from store if data exists
    if (store.tableData.length > 0 && !store.needsRefresh) {
      setTableData(store.tableData)
      setIsloading(false)
      setIsRefetching(false)
    } else {
      // Fetch data if not loaded or refresh is needed
      if (store.needsRefresh) {
        store.needsRefresh = false
      }
      fetchData()
    }
  }, [store, fetchData])

  // Restore scroll position after table is rendered and data is loaded
  useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading)

  const columns = useMemo(() => COLUMNS, [])

  const getRowSearchValue = useMemo(
    () => (row, field) =>
      field === 'fullName' ? [row.firstName, row.lastName].filter(Boolean).join(' ') : row[field],
    []
  )
  const data = useFilteredBySearch(tableData, globalFilter, searchField, getRowSearchValue)

  const table = useMaterialReactTable({
    columns,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      globalFilter: globalFilter,
      showGlobalFilter: false,
      pagination: pagination
    },
    icons: {
      ViewColumnIcon: ColumnsIcon,
    },
    muiFilterDatePickerProps: {
      format: 'DD/MM/YYYY',
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Save scroll position before navigation
        if (tableContainerRef.current) {
          store.scrollTop = tableContainerRef.current.scrollTop
        }
        navigate(`/speaker/${row.original.objectId}`)
      },
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbar: ({ table }) => (
      <TableToolbarWithSearch
        table={table}
        exportFileName="speakers"
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        selectMinWidth={160}
        action={
          <Button
            variant="contained"
            size="large"
            onClick={() => setOpen(true)}
          >
            <Add />
            Add Speaker
          </Button>
        }
      />
    ),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      },
      onScroll: (e) => {
        // Save scroll position while scrolling
        if (e.target instanceof HTMLElement) {
          store.scrollTop = e.target.scrollTop
        }
      }
    }
  })

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        paddingTop={2}
        paddingBottom={1}
      >
        <Typography
          variant="h4"
        >
          Speakers
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add Speaker"}
        view={
          <SpeakersAdd publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchData()
          }} />}
      />
    </>
  )
}
